import React, { useCallback, useEffect, useState, useRef, createContext, useContext } from 'react';
import { useAuth } from './AuthContext';
import { getUserCart, setUserCart, subscribeToUserCart, clearUserCart, type PersistedCartItem } from '../utils/firebase';
export type CartItemType = 'book' | 'supplement';
export interface CartItem {
  productId: string;
  name: string;
  unitPrice: number; // in cents
  quantity: number;
  type: CartItemType;
  /** Stripe Price ID — used if the product has a stable Price ID */
  stripePriceId?: string;
  /** Stripe price lookup_key — resolves to whichever active price has it */
  lookupKey?: string;
  /** Optional thumbnail label or color for rendering in cart drawer */
  accent?: string;
}
interface CartContextValue {
  items: CartItem[];
  itemCount: number;
  subtotal: number; // in cents
  isOpen: boolean;
  openCart: () => void;
  closeCart: () => void;
  toggleCart: () => void;
  addItem: (item: Omit<CartItem, 'quantity'>, quantity?: number) => void;
  removeItem: (productId: string) => void;
  updateQuantity: (productId: string, quantity: number) => void;
  clearCart: () => void;
}
const CartContext = createContext<CartContextValue | undefined>(undefined);
// LocalStorage key for the guest (logged-out) cart so it survives refreshes
// and browser restarts before the user signs in.
const GUEST_CART_KEY = 'sn_guest_cart';
function readGuestCart(): CartItem[] {
  if (typeof window === 'undefined') return [];
  try {
    const raw = window.localStorage.getItem(GUEST_CART_KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed) ? parsed as CartItem[] : [];
  } catch {
    return [];
  }
}
function writeGuestCart(items: CartItem[]) {
  if (typeof window === 'undefined') return;
  try {
    if (items.length === 0) {
      window.localStorage.removeItem(GUEST_CART_KEY);
    } else {
      window.localStorage.setItem(GUEST_CART_KEY, JSON.stringify(items));
    }
  } catch {
    // Ignore storage errors (e.g. private mode quota).
  }
}
// Strip undefined fields before persisting — Firestore rejects undefined values.
function toPersisted(items: CartItem[]): PersistedCartItem[] {
  return items.map(i => {
    const base: PersistedCartItem = {
      productId: i.productId,
      name: i.name,
      unitPrice: i.unitPrice,
      quantity: i.quantity,
      type: i.type
    };
    if (i.stripePriceId) base.stripePriceId = i.stripePriceId;
    if (i.lookupKey) base.lookupKey = i.lookupKey;
    if (i.accent) base.accent = i.accent;
    return base;
  });
}
// Combine two cart lists, summing quantities when productIds collide.
function mergeCarts(a: CartItem[], b: CartItem[]): CartItem[] {
  const map = new Map<string, CartItem>();
  for (const item of a) {
    map.set(item.productId, {
      ...item
    });
  }
  for (const item of b) {
    const existing = map.get(item.productId);
    if (existing) {
      map.set(item.productId, {
        ...existing,
        quantity: existing.quantity + item.quantity
      });
    } else {
      map.set(item.productId, {
        ...item
      });
    }
  }
  return Array.from(map.values());
}
export function CartProvider({
  children
}: {
  children: ReactNode;
}) {
  const {
    user,
    loading: authLoading
  } = useAuth();
  // Hydrate the guest cart from localStorage on first mount so logged-out
  // carts persist across refreshes and browser restarts.
  const [items, setItems] = useState<CartItem[]>(() => readGuestCart());
  const [isOpen, setIsOpen] = useState(false);
  // True once the cart for the CURRENT auth state is loaded.
  // Used to gate Firestore writes so we don't echo our own initial reads.
  const [hydrated, setHydrated] = useState(false);
  // Track which uid we are currently synced to so we can detect transitions.
  const syncedUidRef = useRef<string | null>(null);
  // Tracks the previous auth uid purely so a dedicated effect can detect a
  // signed-in -> signed-out transition and force the guest cart empty.
  const lastUidRef = useRef<string | null>(null);
  // Guard to prevent infinite loops: when the Firestore listener echoes a
  // change back to us, we accept it without re-writing. Also true while a
  // persist write is in flight.
  const writingRef = useRef(false);
  // The most recent cart that still needs to be written to the account.
  // Lets us reliably persist EVERY change: if a write is already in flight,
  // we stash the newest state here and flush it the moment that write
  // finishes — so no change (including the last one before logout) is ever
  // silently dropped.
  const pendingItemsRef = useRef<CartItem[] | null>(null);
  // Reliable, self-draining writer. Writes the pending cart to the given
  // account, then re-checks for newer pending state and flushes again.
  const flushWrite = useCallback((uid: string) => {
    if (writingRef.current) return; // a write is running; it will re-drain after
    const next = pendingItemsRef.current;
    if (next === null) return;
    pendingItemsRef.current = null;
    writingRef.current = true;
    setUserCart(uid, toPersisted(next)).catch(err => console.error('[Cart] Failed to persist:', err)).finally(() => {
      writingRef.current = false;
      // A newer change arrived while we were writing — flush it now.
      if (pendingItemsRef.current !== null) flushWrite(uid);
    });
  }, []);
  // ── Auth transitions: sign-in merge, sign-out clear ─────────────────
  useEffect(() => {
    if (authLoading) return;
    // Block cart persistence until the cart for the CURRENT auth state has
    // actually loaded. Without this reset, `hydrated` stays true across a
    // logout -> login, so the persist effect fires on sign-in with the empty
    // post-logout cart and overwrites the saved Firestore cart before we get
    // a chance to read it back.
    setHydrated(false);
    let cancelled = false;
    let unsub: (() => void) | null = null;
    async function syncWithAuth() {
      if (user) {
        // SIGN-IN PATH
        const guestItems = syncedUidRef.current === null ? items : [];
        try {
          // Load this user's existing Firestore cart
          const remote = await getUserCart(user.uid);
          if (cancelled) return;
          const remoteItems: CartItem[] = remote.map(i => ({
            ...i
          }));
          // Merge guest items on top of account cart (sum on collisions)
          const merged = guestItems.length > 0 ? mergeCarts(remoteItems, guestItems) : remoteItems;
          // If we actually added guest items, write the merged result back
          if (guestItems.length > 0) {
            writingRef.current = true;
            try {
              await setUserCart(user.uid, toPersisted(merged));
            } finally {
              writingRef.current = false;
            }
          }
          // Guest items now live on the account cart — clear the local
          // guest copy so it isn't re-merged on a future logged-out session.
          writeGuestCart([]);
          if (cancelled) return;
          setItems(merged);
          syncedUidRef.current = user.uid;
          setHydrated(true);
          // Subscribe to real-time changes across devices/tabs
          const subscribedUid = user.uid;
          unsub = subscribeToUserCart(subscribedUid, remoteItems => {
            // Don't let a server snapshot clobber the local cart while we
            // still have a write in flight OR a newer change queued to be
            // written. Otherwise an intermediate server state can overwrite
            // a just-added item before its own save lands.
            if (writingRef.current || pendingItemsRef.current !== null) return;
            // Ignore trailing snapshots that arrive after we've signed out or
            // switched users. Without this guard, a late update from the old
            // account's listener would re-fill the cart right after sign-out
            // cleared it — leaving the "guest" cart stuck with the previous
            // user's items.
            if (syncedUidRef.current !== subscribedUid) return;
            setItems(remoteItems.map(i => ({
              ...i
            })));
          });
        } catch (err) {
          console.error('[Cart] Failed to sync with Firestore:', err);
          if (!cancelled) setHydrated(true);
        }
      } else {
        // SIGN-OUT PATH (or initial unauthenticated state).
        // The actual cart-clearing + account flush on sign-out is handled
        // authoritatively by the dedicated logout effect below. Here we only
        // reset the sync bookkeeping so the live-sync listener guard drops
        // any trailing snapshots from the previous account.
        syncedUidRef.current = null;
        setHydrated(true);
      }
    }
    syncWithAuth();
    return () => {
      cancelled = true;
      if (unsub) unsub();
    };
    // We intentionally exclude `items` from deps — we only want this
    // to fire on auth transitions, not every cart mutation. The guest
    // items snapshot is captured at the moment of sign-in.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.uid, authLoading]);
  // ── Force an empty guest cart the instant the user signs out ─────────
  // This is the single, authoritative cart-clear on logout. It runs
  // synchronously on the render where `user` flips to null. The account
  // cart is already kept up to date by the reliable persist effect below,
  // so we don't need a fragile last-second flush here — we just empty the
  // on-screen cart + guest storage. Any in-flight/pending write keeps
  // targeting the departing account, so the latest cart is still saved for
  // the next sign-in.
  useEffect(() => {
    const prevUid = lastUidRef.current;
    const currUid = user?.uid ?? null;
    lastUidRef.current = currUid;
    // Only act on a genuine signed-in -> signed-out transition. On the very
    // first mount prevUid is null, so a real guest's hydrated localStorage
    // cart is left untouched.
    if (prevUid && !currUid) {
      setItems([]);
      writeGuestCart([]);
    }
  }, [user?.uid]);
  // ── Reliably persist cart changes to the account when signed in ──────
  // Every change is queued into pendingItemsRef and drained by flushWrite,
  // which never drops a write: if a save is mid-flight when a new change
  // arrives, the newest state is written as soon as the current save ends.
  useEffect(() => {
    // Only persist once we are fully synced to the CURRENT user's cart.
    // Requiring syncedUidRef to match the active uid guarantees we never
    // write the transient empty cart that exists in the brief window between
    // sign-in and the saved cart finishing its load — which would otherwise
    // wipe the account's saved cart on login.
    if (!user || !hydrated || syncedUidRef.current !== user.uid) return;
    pendingItemsRef.current = items;
    flushWrite(user.uid);
  }, [items, user?.uid, hydrated, flushWrite]);
  // ── Persist the guest cart to localStorage when logged out ──────────
  // Mirrors every cart mutation while no user is signed in so the cart
  // survives a page refresh or browser restart.
  useEffect(() => {
    if (user) return;
    writeGuestCart(items);
  }, [items, user]);
  const itemCount = items.reduce((sum, i) => sum + i.quantity, 0);
  const subtotal = items.reduce((sum, i) => sum + i.unitPrice * i.quantity, 0);
  const value: CartContextValue = {
    items,
    itemCount,
    subtotal,
    isOpen,
    openCart: () => setIsOpen(true),
    closeCart: () => setIsOpen(false),
    toggleCart: () => setIsOpen(v => !v),
    addItem: (item, quantity = 1) => {
      setItems(prev => {
        const existing = prev.find(i => i.productId === item.productId);
        if (existing) {
          return prev.map(i => i.productId === item.productId ? {
            ...i,
            quantity: i.quantity + quantity
          } : i);
        }
        return [...prev, {
          ...item,
          quantity
        }];
      });
      setIsOpen(true);
    },
    removeItem: productId => {
      setItems(prev => prev.filter(i => i.productId !== productId));
    },
    updateQuantity: (productId, quantity) => {
      if (quantity <= 0) {
        setItems(prev => prev.filter(i => i.productId !== productId));
        return;
      }
      setItems(prev => prev.map(i => i.productId === productId ? {
        ...i,
        quantity
      } : i));
    },
    clearCart: () => {
      setItems([]);
      if (user) {
        writingRef.current = true;
        clearUserCart(user.uid).catch(err => console.error('[Cart] Failed to clear:', err)).finally(() => {
          writingRef.current = false;
        });
      }
    }
  };
  return <CartContext.Provider value={value} data-id="element-3511">{children}</CartContext.Provider>;
}
export function useCart() {
  const ctx = useContext(CartContext);
  if (!ctx) throw new Error('useCart must be used inside <CartProvider>');
  return ctx;
}